<script setup>
  import { ref, onMounted, computed, onUnmounted, watch } from "vue";
  const isLoggedIn = ref(localStorage.getItem("isLoggedIn") === "true")
  const MenuData = ref([]);
  const searchInput = ref(null);
  const searchContainer = ref(null);
  const isLoading = ref(false);
  const props = defineProps({
    locale: {
      type: String,
      required: true,
      default: "en"
    },
    isAuthenticated: Boolean,
  });

  const query = ref("");
  const isMenuOpen = ref(false);
  const searchHistory = ref([]);
  const dropdownQuery = ref("");

  const allowedNames = ref([]);

  async function performSearch(term) {
    if (term.length < 2) {
      allowedNames.value = [];
      return;
    }

    isLoading.value = true;
    try {
      const requestUrl = `/integrations/searchProducts?q=${encodeURIComponent(term)}`;
      const cacheKey = `search_products_cache:${requestUrl}`;
      const cachedData = localStorage.getItem(cacheKey);
      let data;

      if (cachedData) {
        data = JSON.parse(cachedData);
      } else {
        const response = await fetch(requestUrl);
        data = await response.json();
        localStorage.setItem(cacheKey, JSON.stringify(data));
      }

      allowedNames.value = Object.values(data)
        .filter(item => item && item.label)
        .map(item => item.label.toLowerCase());

    } catch (error) {
      console.error("Erro na pesquisa:", error);
    } finally {
      isLoading.value = false;
    }
  }

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;

    if (isMenuOpen.value) {
      nextTick(() => {
        if (searchInput.value) {
          searchInput.value.focus();
        }
      });
    }
  };


  const navigateToCategoryWithSearch = (event, link) => {
    event.preventDefault();

    const url = new URL(window.location.origin + link);
    if (dropdownQuery.value) {
      url.searchParams.set('q', dropdownQuery.value);
    }
    window.location.href = url.toString();
  };

  const isEnabled = (label) => {
    if (dropdownQuery.value.length < 2) return true;
    return allowedNames.value.includes(label.toLowerCase());
  };

  watch(dropdownQuery, (newVal) => {
    performSearch(newVal.trim());
  });


  const searchAction = computed(() => {
    return `/${props.locale}/search`;
  });

  const handleClickOutside = (event) => {
    if (searchContainer.value && !searchContainer.value.contains(event.target)) {
      isMenuOpen.value = false;
    }
  };

  const translations = {
    en: "Search tiles, collections, or styles...",
  };

  const trans = (key) => {
    return _.get(window.translations, key, key);
  };

  const searchPlaceholder = computed(() => {
    return translations[props.locale] || translations.en;
  });


  function handleSearch() {
    const searchTerm = query.value.trim();
    if (!searchTerm) return;

    const newHistory = [
      searchTerm,
      ...searchHistory.value.filter(item => item !== searchTerm)
    ].slice(0, 5);

    searchHistory.value = newHistory;
    localStorage.setItem('search_history', JSON.stringify(newHistory));

    isMenuOpen.value = false;

    // Faz o redirecionamento
    window.location.href = `${searchAction.value}?q=${encodeURIComponent(searchTerm)}`;
  }

  function clearSearch() {
    query.value = "";
    if (searchInput.value) searchInput.value.focus();
  }

  function selectHistory(item) {
    query.value = item;
    isMenuOpen.value = false;
    handleSearch();
  }

  function toggleDropdown(menuitem) {
    MenuData.value.forEach(item => {
      if (item !== menuitem) item.isOpen = false;
    });
    menuitem.isOpen = !menuitem.isOpen;
  }

  const fetchMenuData = async () => {
    try {
      if (MenuData.value.length === 0) {
        const cacheKey = "menu_data_cache:/api/getmenu";
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          MenuData.value = JSON.parse(cachedData);
        }

        const response = await fetch("/api/getmenu");
        const data = await response.json();
        localStorage.setItem(cacheKey, JSON.stringify(data));
        MenuData.value = data;
      }
    } catch (e) {
      console.error("Erro ao carregar menu", e);
    }
  };


  onMounted(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener('click', handleClickOutside);

    const saved = localStorage.getItem('search_history');
    if (saved) searchHistory.value = JSON.parse(saved);

    const urlParams = new URLSearchParams(window.location.search);
    const qFromUrl = urlParams.get('q');
    if (qFromUrl) {
      query.value = qFromUrl;
    }
    fetchMenuData();
  });

  onUnmounted(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener('click', handleClickOutside)
  });
</script>

<template>
  <nav class="navbar nav-menu" id="nav-container">
    <div class="nav-container">
      <div class="logo">
        <a href="/">
          <svg xmlns="http://www.w3.org/2000/svg" width="114" height="80" viewBox="0 0 145 80" fill="none">
            <g clip-path="url(#clip0_206_250)">
              <path d="M144.198 57.5283H5.15527V80H144.198V57.5283Z" fill="#A6A7A7" />
              <path d="M24.0674 39.9616H15.3704L7.85059 47.8958H16.5455L24.0674 39.9616Z" fill="#155B98" />
              <path
                d="M16.7021 48.2593H7.00244L15.2143 39.5961H24.916L16.7041 48.2593H16.7021ZM8.69922
                47.5303H16.3875L23.2192 40.3251H15.531L8.70125 47.5303H8.69922Z"
                fill="#191915" />
              <path
                d="M5.76001 46.9871L13.2331 39.3073V20.9935H27.7227V14.9212H13.225L13.2331 0.850162L5.76001
                7.93013L5.7803 14.9212H0.365234V20.8562H5.7803L5.76001 46.9871Z"
                fill="#155B98" />
              <path
                d="M5.39274 47.8877L5.41304 21.2197H0V14.5578H5.41304L5.39274 7.77464L13.5986 0L13.5904
                14.5578H28.0881V21.357H13.5986V39.4548L5.39274 47.8877ZM0.732699 20.4927H6.14777L6.12747
                46.0884L12.8679 39.162V20.63H27.3575V15.2868H12.8598L12.8659 1.70032L6.12544 8.08562L6.14574
                15.2847H0.732699V20.4907V20.4927Z"
                fill="#191915" />
              <path d="M77.4021 13.954L68.4312 23.0634V13.8187H77.4021V13.954Z" fill="#008FCF" />
              <path d="M77.4021 13.954L68.4312 23.0634V13.8187H77.4021V13.954Z" fill="#155B98" />
              <path
                d="M68.0659 23.956V13.4552H77.7676V14.1034L68.0659 23.956ZM68.7986 14.1821V22.1708L76.6655
                14.1821H68.7986Z" fill="#191915" />
              <path d="M93.4102 47.8716L102.379 38.7662V48.0109H93.4102V47.8716Z" fill="#155B98" />
              <path
                d="M102.746 48.3744H93.0425V47.7221L102.746 37.8716V48.3724V48.3744ZM94.1446
                47.6454H102.013V39.6567L94.1446 47.6454Z" fill="#191915" />
              <path
                d="M93.1603 35.6866C91.5467 38.5279 88.4941 40.4402 84.993 40.4402C79.8113 40.4402 75.61 36.2419
                75.61 31.0622C75.61 25.8825 79.8113 21.6821 84.993 21.6821C88.5794 21.6821 91.6949 23.6935 93.2759
                26.6458H102.381V13.8187H80.8546L68.5732 26.0965V47.9705H90.0996L102.219 35.5937L93.1623
                35.6866H93.1603Z" fill="#155B98" />
              <path
                d="M90.252 48.334H68.2041V25.9451L80.7006 13.4532H102.746V27.0093H93.057L92.9535 26.8154C91.3785
                 23.8732 88.3279 22.0456 84.9932 22.0456C80.0206 22.0456 75.9756 26.0905 75.9756 31.0602C75.9756
                 36.0299 80.0206 40.0747 84.9932 40.0747C88.2346 40.0747 91.2425 38.3239 92.8418 35.5048L92.9454
                 35.3211L103.094 35.2161L90.252 48.33V48.334ZM68.9368 47.607H89.9435L101.34 35.9673L93.3716 36.0501C91.6159
                 38.9903 88.4254 40.8057 84.9912 40.8057C79.6147 40.8057 75.2408 36.4358 75.2408 31.0622C75.2408 25.6886
                 79.6147 21.3187 84.9912 21.3187C88.5228 21.3187 91.7601 23.2149 93.4913 26.2823H102.014V14.1822H81.005L68.9368
                 26.246V47.605V47.607Z"
                fill="#191915" />
              <path d="M26.6187 32.1688H32.1393L26.6187 37.6858V32.1688Z" fill="#A6A7A7" />
              <path
                d="M26.2534 38.5683V31.8053H33.0202L26.2534 38.5683ZM26.9841 32.5323V36.8033L31.2565 32.5323H26.9841Z"
                fill="#191915" />
              <path d="M44.9058 47.8372H39.3872L44.9058 42.3183V47.8372Z" fill="#A6A7A7" />
              <path
                d="M45.2711 48.2007H38.5063L45.2711 41.4338V48.2007ZM40.2681 47.4717H44.5405V43.1987L40.2681 47.4717Z"
                fill="#191915" />
              <path
                d="M35.4517 36.4055C33.3734 36.4055 31.6888 38.0897 31.6888 40.1676C31.6888 42.2456 33.3734 43.9317
                35.4517 43.9317C37.5301 43.9317 39.2147 42.2476 39.2147 40.1676C39.2147 38.0876 37.5301 36.4055 35.4517
                36.4055ZM26.6187 40.0303L34.4856 32.1648H44.7616L44.7474 40.0222L36.8664 47.8938H26.6167V40.0283L26.6187
                40.0303Z"
                fill="#A6A7A7" />
              <path
                d="M37.0206 48.2593H26.2534V39.8809L34.3334 31.8033H45.129L45.1148 40.1757L37.0206 48.2613V48.2593ZM26.9841
                47.5303H36.7142L44.3801 39.8728L44.3923 32.5303H34.6338L26.9821 40.1817V47.5323L26.9841 47.5303ZM35.4517
                44.2952C33.1745 44.2952 31.3214 42.4435 31.3214 40.1676C31.3214 37.8918 33.1745 36.042 35.4517
                36.042C37.729 36.042 39.582 37.8938 39.582 40.1676C39.582 42.4414 37.729 44.2952 35.4517
                44.2952ZM35.4517 36.769C33.5784 36.769 32.0541 38.2936 32.0541 40.1676C32.0541 42.0416 33.5784 43.5662
                35.4517 43.5662C37.3251 43.5662 38.8493 42.0416 38.8493 40.1676C38.8493 38.2936 37.3251 36.769 35.4517
                36.769Z"
                fill="#191915" />
              <path d="M105.553 32.1688H111.074L105.553 37.6858V32.1688Z" fill="#A6A7A7" />
              <path
                d="M105.188 38.5683V31.8053H111.955L105.188 38.5683ZM105.921 32.5323V36.8033L110.193 32.5323H105.921Z"
                fill="#191915" />
              <path d="M123.696 47.8958H118.92L123.696 43.2775V47.8958Z" fill="#A6A7A7" />
              <path
                d="M124.061 48.2593H118.017L124.061 42.4152V48.2593ZM119.821 47.5303H123.329V44.1377L119.821 47.5303Z"
                fill="#191915" />
              <path
                d="M117.727 38.2068C117.061 37.1486 115.728 36.4055 114.386 36.4055C113.045 36.4055 111.75 37.2031
                111.074 38.2068H117.727ZM110.635 40.4705C110.788 42.4071 112.409 43.9317 114.384 43.9317C115.253
                43.9317 116.053 43.6369 116.688 43.1442L121.299 43.1018L116.312 47.8958H105.553V40.0303L113.418
                32.1648H123.615V40.416L110.635 40.4685V40.4705Z"
                fill="#A6A7A7" />
              <path
                d="M116.463 48.2593H105.188V39.8809L113.266 31.8013H123.98V40.7795L111.054 40.83C111.364 42.391 112.756
                43.5642 114.384 43.5642C115.145 43.5642 115.864 43.3179 116.465 42.8534L116.562 42.7767L122.215
                42.7242L116.463 48.2553V48.2593ZM105.921 47.5303H116.166L120.386 43.4733L116.814 43.5057C116.105
                44.0226 115.267 44.2952 114.386 44.2952C112.247 44.2952 110.439 42.6272 110.272 40.4968L110.242
                40.105L123.252 40.0525V32.5263H113.574L105.923 40.1777V47.5283L105.921 47.5303ZM118.387
                38.5703H110.386L110.767 38.0028C111.547 36.8477 113.033 36.04 114.384 36.04C115.827 36.04 117.295
                36.8316 118.036 38.0109L118.385 38.5683L118.387 38.5703ZM111.859 37.8433H116.968C116.304 37.185
                115.34 36.771 114.384 36.771C113.499 36.771 112.541 37.191 111.857 37.8433H111.859Z"
                fill="#191915" />
              <path d="M126.907 32.1688H132.426L126.907 37.6858V32.1688Z" fill="#A6A7A7" />
              <path d="M126.54 38.5683V31.8053H133.306L126.54 38.5683ZM127.272 32.5323V36.8033L131.545 32.5323H127.272Z"
                    fill="#191915" />
              <path
                d="M131.693 47.8958H126.905V40.0303L134.772 32.1688H142.842V40.309H139.588C139.588 40.309 139.493
                36.3994 135.549 36.3994C132.891 36.3994 131.679 38.3078 131.679 39.2811C131.679 40.832 131.695 47.8958
                131.695 47.8958"
                fill="#A6A7A7" />
              <path
                d="M132.06 48.2593H126.54V39.8809L134.62 31.8053H143.207V40.6745H139.229L139.221 40.3191C139.217
                40.1737 139.079 36.7649 135.547 36.7649C133.051 36.7649 132.044 38.54 132.044 39.2811C132.044
                40.834 132.06 47.8978 132.06 47.8978V48.2633V48.2593ZM127.272 47.5303H131.327C131.323 46.0642
                131.311 40.6179 131.311 39.2771C131.311 38.1805 132.59 36.0339 135.547 36.0339C139.062 36.0339
                139.775 38.9196 139.919 39.9435H142.476V32.5303H134.924L127.272 40.1797V47.5303Z"
                fill="#191915" />
              <path d="M47.1812 32.1688H52.6997L47.1812 37.6858V32.1688Z" fill="#A6A7A7" />
              <path
                d="M46.8154 38.5683V31.8053H53.5802L46.8154 38.5683ZM47.5461 32.5323V36.8033L51.8185 32.5323H47.5461Z"
                fill="#191915" />
              <path d="M65.4678 47.8958H59.9492L65.4678 42.3768V47.8958Z" fill="#A6A7A7" />
              <path d="M65.8351 48.2593H59.0703L65.8351 41.4943V48.2593ZM60.83 47.5303H65.1024V43.2593L60.83 47.5303Z"
                    fill="#191915" />
              <path
                d="M56.2455 36.4055C54.1672 36.4055 52.4826 38.0897 52.4826 40.1676C52.4826 42.2456 54.1672 43.9317
                 56.2455 43.9317C58.3239 43.9317 60.0085 42.2476 60.0085 40.1676C60.0085 38.0876 58.3239 36.4055
                 56.2455 36.4055ZM47.1812 40.0303L55.046 32.1648H65.3281L65.3099 40.0222L57.4308 47.8938H52.8398L52.9778
                 62.9867L47.1832 67.815V40.0283L47.1812 40.0303Z"
                fill="#A6A7A7" />
              <path
                d="M46.8154 68.5965V39.8809L54.8934 31.8013H65.6951L65.6748 40.1737L57.5826 48.2593H53.2088L53.3448
                63.1563L46.8154 68.5965ZM47.5461 40.1817V67.0376L52.608 62.8191L52.468 47.5323H57.2762L64.9421
                39.8748L64.9604 32.5323H55.1978L47.5461 40.1838V40.1817ZM56.2451 44.2952C53.9679 44.2952 52.1168
                42.4435 52.1168 40.1676C52.1168 37.8918 53.9679 36.042 56.2451 36.042C58.5224 36.042 60.3754 37.8938
                60.3754 40.1676C60.3754 42.4414 58.5224 44.2952 56.2451 44.2952ZM56.2451 36.771C54.3718 36.771
                52.8495 38.2956 52.8495 40.1696C52.8495 42.0436 54.3738 43.5683 56.2451 43.5683C58.1164 43.5683 59.6427
                42.0436 59.6427 40.1696C59.6427 38.2956 58.1185 36.771 56.2451 36.771Z"
                fill="#191915" />
              <path
                d="M139.828 13.3704C140.782 13.3704 141.6 13.7076 142.28 14.3861C142.96 15.0626 143.301 15.8825
                143.301 16.8376C143.301 17.7928 142.96 18.6127 142.28 19.2912C141.6 19.9717 140.782 20.311 139.828
                20.311C138.874 20.311 138.038 19.9737 137.358 19.2952C136.676 18.6187 136.337 17.7989 136.337
                16.8376C136.337 15.8764 136.676 15.0626 137.358 14.3861C138.04 13.7076 138.864 13.3704 139.828
                13.3704ZM139.822 13.8489C139.008 13.8489 138.31 14.1397 137.733 14.7213C137.155 15.3029 136.867
                16.0097 136.867 16.8376C136.867 17.6656 137.155 18.3704 137.733 18.958C138.312 19.5416 139.008
                19.8324 139.822 19.8324C140.636 19.8324 141.332 19.5416 141.906 18.958C142.481 18.3704 142.767
                17.6656 142.767 16.8376C142.767 16.0097 142.481 15.309 141.906 14.7233C141.332 14.1377 140.636
                13.8469 139.822 13.8469V13.8489ZM138.164 18.748V14.8627H140.153C141.031 14.8627 141.47 15.2645
                141.47 16.0723C141.47 16.6539 141.271 17.0073 140.869 17.1224C141.238 17.2132 141.425 17.4677
                141.425 17.8877V18.75H140.603V18.0331C140.603 17.6656 140.412 17.4838 140.033
                17.4838H138.986V18.75H138.164V18.748ZM138.986 16.7831H140.033C140.467 16.7831 140.684 16.5792 140.684
                16.1652C140.684 15.7512 140.483 15.5614 140.084 15.5614H138.986V16.7831Z"
                fill="#191915" />
              <path
                d="M16.4585 76.5933V69.7092H17.3739V76.5933H16.4585ZM18.8799 76.5933V71.6074H19.645V72.3162C20.0124
                71.769 20.5442 71.4943 21.2403 71.4943C21.5427 71.4943 21.8208 71.5489 22.0725 71.6559C22.3262 71.7649
                22.5149 71.9063 22.6408 72.082C22.7666 72.2577 22.8539 72.4657 22.9046 72.706C22.9351 72.8635 22.9513
                73.1361 22.9513 73.5279V76.5933H22.1009V73.5602C22.1009 73.2149 22.0684 72.9584 22.0014 72.7888C21.9345
                72.6191 21.8188 72.4818 21.6503 72.3809C21.4818 72.2779 21.285 72.2274 21.0577 72.2274C20.6964 72.2274
                20.3838 72.3425 20.12 72.5707C19.8581 72.7989 19.7262 73.233 19.7262 73.8712V76.5953H18.8758L18.8799
                76.5933ZM27.5058 76.5933V75.9632C27.1872 76.458 26.7203 76.7044 26.1033 76.7044C25.7035 76.7044 25.3361
                76.5953 25.0012 76.3752C24.6663 76.1571 24.4066 75.8502 24.2219 75.4564C24.0372 75.0646 23.9458 74.6123
                23.9458 74.1014C23.9458 73.5905 24.029 73.1523 24.1955 72.7464C24.3619 72.3405 24.6115 72.0295 24.9464
                71.8134C25.2793 71.5973 25.6527 71.4903 26.0648 71.4903C26.3672 71.4903 26.6351 71.5529 26.8726
                71.6801C27.108 71.8073 27.3008 71.9729 27.449 72.1749V69.7052H28.2933V76.5893H27.5058V76.5933ZM24.8206
                74.1034C24.8206 74.7415 24.9566 75.2201 25.2265 75.5351C25.4965 75.8522 25.8171 76.0097 26.1845
                76.0097C26.5519 76.0097 26.8705 75.8582 27.1303 75.5573C27.3901 75.2544 27.52 74.794 27.52
                74.1741C27.52 73.4915 27.3881 72.9907 27.1242 72.6716C26.8604 72.3526 26.5336 72.193 26.148
                72.193C25.7623 72.193 25.4559 72.3465 25.2022 72.6535C24.9485 72.9604 24.8226 73.443 24.8226
                74.1034H24.8206ZM32.9148 76.5933V75.8603C32.5251 76.4237 31.9954 76.7044 31.3236 76.7044C31.0272
                76.7044 30.7512 76.6478 30.4955 76.5347C30.2397 76.4216 30.0489 76.2803 29.9251 76.1107C29.8013
                75.939 29.714 75.731 29.6633 75.4846C29.6288 75.3191 29.6105 75.0565 29.6105 74.6951V71.6054H30.461V74.372C30.461
                74.8142 30.4772 75.1111 30.5137 75.2645C30.5665 75.4867 30.6802 75.6603 30.8527 75.7876C31.0252 75.9148
                31.2403 75.9774 31.494 75.9774C31.7477 75.9774 31.9872 75.9128 32.2105 75.7815C32.4338 75.6523 32.5921
                75.4746 32.6854 75.2504C32.7788 75.0263 32.8255 74.7011 32.8255 74.275V71.6034H33.6759V76.5893H32.9168L32.9148
                76.5933ZM31.1307 70.9855L31.7579 69.6708H32.8722L31.835 70.9855H31.1328H31.1307ZM34.6664 75.105L35.5066
                74.9737C35.5533 75.309 35.6852 75.5654 35.9004 75.7431C36.1155 75.9208 36.4179 76.0097 36.8036 76.0097C37.1892
                76.0097 37.4835 75.9309 37.6723 75.7714C37.861 75.6139 37.9544 75.4281 37.9544 75.2161C37.9544 75.0242
                37.8712 74.8748 37.7047 74.7657C37.589 74.691 37.2988 74.5941 36.836 74.479C36.2129 74.3215 35.7806
                74.1862 35.5411 74.0731C35.2996 73.958 35.1169 73.8005 34.9931 73.5985C34.8693 73.3966 34.8064
                73.1725 34.8064 72.9301C34.8064 72.708 34.8571 72.502 34.9607 72.3122C35.0642 72.1224 35.2022
                71.9649 35.3788 71.8397C35.5107 71.7427 35.6913 71.6599 35.9186 71.5933C36.146 71.5267 36.3916
                71.4923 36.6534 71.4923C37.0471 71.4923 37.3922 71.5489 37.6885 71.662C37.9848 71.775 38.206 71.9285
                38.3461 72.1204C38.4882 72.3122 38.5856 72.5707 38.6384 72.8918L37.8082 73.0048C37.7697 72.7484
                37.6621 72.5485 37.4794 72.4031C37.2988 72.2597 37.0431 72.187 36.7122 72.187C36.3225 72.187 36.0445
                72.2516 35.876 72.3788C35.7076 72.5061 35.6264 72.6575 35.6264 72.8292C35.6264 72.9382 35.6609
                73.0372 35.7299 73.124C35.7989 73.2149 35.9085 73.2896 36.0546 73.3502C36.1399 73.3825 36.3895
                73.4531 36.8056 73.5662C37.4064 73.7258 37.8265 73.857 38.064 73.958C38.3014 74.061 38.4882 74.2084
                38.6221 74.4023C38.7581 74.5961 38.8251 74.8384 38.8251 75.1252C38.8251 75.4119 38.7419 75.6725
                38.5775 75.9208C38.4131 76.1692 38.1736 76.3631 37.863 76.4984C37.5525 76.6337 37.1993 76.7023
                36.8056 76.7023C36.1541 76.7023 35.6589 76.567 35.3158 76.2985C34.9749 76.0299 34.7557 75.63 34.6623
                75.101L34.6664 75.105ZM41.693 75.836L41.8168 76.5832C41.5773 76.6337 41.3642 76.6579 41.1754
                76.6579C40.8669 76.6579 40.6274 76.6095 40.4589 76.5125C40.2885 76.4156 40.1687 76.2884 40.0997
                76.1288C40.0307 75.9693 39.9962 75.6381 39.9962 75.1313V72.2617H39.3731V71.6054H39.9962V70.3695L40.8405
                69.8627V71.6054H41.695V72.2617H40.8405V75.1777C40.8405 75.418 40.8547 75.5735 40.8852 75.6422C40.9156
                75.7108 40.9643 75.7653 41.0313 75.8057C41.0983 75.8461 41.1957 75.8663 41.3215 75.8663C41.4169 75.8663
                41.5407 75.8562 41.695 75.834L41.693 75.836ZM42.5129 76.5933V71.6074H43.2781V72.3627C43.473 72.0093
                43.6536 71.775 43.818 71.662C43.9824 71.5489 44.1651 71.4923 44.364 71.4923C44.6501 71.4923 44.9404
                71.5832 45.2367 71.7649L44.9444 72.5485C44.7374 72.4253 44.5284 72.3647 44.3213 72.3647C44.1366
                72.3647 43.9682 72.4192 43.8221 72.5323C43.6739 72.6434 43.5683 72.7968 43.5054 72.9947C43.41 73.2956
                43.3634 73.6228 43.3634 73.9802V76.5913H42.5129V76.5933ZM45.7461 70.6805V69.7092H46.5966V70.6805H45.7461ZM45.7461
                76.5933V71.6074H46.5966V76.5933H45.7461ZM51.1592 75.9774C50.8446 76.2439 50.5422 76.4317 50.2499
                76.5408C49.9597 76.6498 49.6471 76.7044 49.3122 76.7044C48.7622 76.7044 48.338 76.5711 48.0437
                76.3025C47.7474 76.0359 47.5992 75.6926 47.5992 75.2767C47.5992 75.0323 47.656 74.8102 47.7677
                74.6082C47.8793 74.4063 48.0254 74.2447 48.2061 74.1216C48.3867 74.0004 48.5917 73.9075 48.817
                73.8449C48.9834 73.8005 49.2351 73.7581 49.572 73.7177C50.258 73.6369 50.7634 73.54 51.0861
                73.4269C51.0902 73.3118 51.0902 73.2371 51.0902 73.2068C51.0902 72.8635 51.009 72.6191 50.8486
                72.4798C50.6315 72.288 50.3088 72.193 49.8805 72.193C49.4807 72.193 49.1864 72.2637 48.9956
                72.401C48.8048 72.5404 48.6648 72.7867 48.5734 73.1401L47.7433 73.0271C47.8184 72.6737 47.9422
                72.3889 48.1168 72.1708C48.2893 71.9527 48.5389 71.7851 48.8677 71.668C49.1945 71.5509 49.574 71.4923
                50.0043 71.4923C50.4346 71.4923 50.7796 71.5428 51.0476 71.6418C51.3155 71.7427 51.5123 71.8679 51.638
                72.0194C51.764 72.1708 51.8513 72.3627 51.902 72.5949C51.9304 72.7383 51.9447 72.9988 51.9447
                73.3744V74.5012C51.9447 75.2867 51.9629 75.7835 51.9995 75.9915C52.036 76.1995 52.107 76.3994 52.2146
                76.5893H51.3276C51.2404 76.4136 51.1835 76.2096 51.1572 75.9733L51.1592 75.9774ZM51.0882
                74.0893C50.7796 74.2145 50.3169 74.3215 49.6999 74.4083C49.3508 74.4588 49.1032 74.5153 48.9591
                74.5779C48.815 74.6405 48.7033 74.7314 48.6242 74.8526C48.545 74.9737 48.5065 75.107 48.5065
                75.2544C48.5065 75.4806 48.5917 75.6684 48.7642 75.8179C48.9367 75.9673 49.1864 76.044 49.5172
                76.044C49.848 76.044 50.1362 75.9733 50.39 75.83C50.6437 75.6866 50.8324 75.4927 50.9522
                75.2464C51.0435 75.0545 51.0882 74.7738 51.0882 74.4002V74.0913V74.0893ZM59.2006
                76.5933V75.9632C58.8819 76.458 58.4151 76.7044 57.7981 76.7044C57.3983 76.7044 57.0309 76.5953 56.696
                76.3752C56.3611 76.1571 56.1013 75.8502 55.9167 75.4564C55.732 75.0646 55.6406 74.6123 55.6406
                74.1014C55.6406 73.5905 55.7238 73.1523 55.8903 72.7464C56.0567 72.3405 56.3063 72.0295 56.6412
                71.8134C56.9741 71.5973 57.3475 71.4903 57.7596 71.4903C58.062 71.4903 58.3299 71.5529 58.5674
                71.6801C58.8028 71.8073 58.9956 71.9729 59.1438 72.1749V69.7052H59.9881V76.5893H59.2006V76.5933ZM56.5154
                74.1034C56.5154 74.7415 56.6514 75.2201 56.9213 75.5351C57.1913 75.8522 57.5119 76.0097 57.8793
                76.0097C58.2467 76.0097 58.5653 75.8582 58.8251 75.5573C59.0849 75.2544 59.2148 74.794 59.2148
                74.1741C59.2148 73.4915 59.0829 72.9907 58.819 72.6716C58.5552 72.3526 58.2284 72.193 57.8428
                72.193C57.4571 72.193 57.1507 72.3465 56.897 72.6535C56.6433 72.9604 56.5174 73.443 56.5174
                74.1034H56.5154ZM64.7557 74.9879L65.6325 75.0949C65.4945 75.6058 65.2367 76.0016 64.8633
                76.2823C64.4898 76.563 64.0108 76.7044 63.4283 76.7044C62.6956 76.7044 62.1131 76.4802 61.6849
                76.0299C61.2546 75.5816 61.0415 74.9515 61.0415 74.1397C61.0415 73.3279 61.2587 72.6494 61.693
                72.187C62.1273 71.7246 62.6895 71.4923 63.3817 71.4923C64.0738 71.4923 64.5994 71.7185 65.0236
                72.1729C65.4478 72.6272 65.6609 73.2653 65.6609 74.0893C65.6609 74.1397 65.6609 74.2145 65.6569
                74.3154H61.9203C61.9508 74.8627 62.107 75.2827 62.3871 75.5735C62.6672 75.8643 63.0163 76.0097
                63.4344 76.0097C63.745 76.0097 64.0108 75.9289 64.2321 75.7653C64.4513 75.6038 64.6258 75.3433
                64.7557 74.9859V74.9879ZM61.967 73.6208H64.7659C64.7273 73.2007 64.6218 72.8877 64.4452
                72.6777C64.1752 72.3526 63.8241 72.189 63.3938 72.189C63.0041 72.189 62.6753 72.3183 62.4095
                72.5788C62.1436 72.8393 61.9974 73.1866 61.969 73.6208H61.967ZM74.429 74.1801L75.3443 74.4103C75.1535
                75.1595 74.8065 75.729 74.3092 76.1228C73.8099 76.5166 73.201 76.7124 72.4805 76.7124C71.76 76.7124
                71.1288 76.561 70.662 76.2601C70.1951 75.9592 69.84 75.521 69.5964 74.9475C69.3528 74.374 69.2311
                73.7601 69.2311 73.1018C69.2311 72.3849 69.3691 71.7589 69.6431 71.2258C69.9191 70.6926 70.3108
                70.2867 70.8182 70.0101C71.3256 69.7334 71.8858 69.5941 72.4967 69.5941C73.1889 69.5941 73.7714
                69.7698 74.2422 70.1191C74.7131 70.4705 75.0419 70.9632 75.2286 71.5993L74.3275 71.8114C74.1671
                71.3106 73.9337 70.9451 73.6293 70.7169C73.3248 70.4887 72.9392 70.3736 72.4785 70.3736C71.9467
                70.3736 71.5022 70.5008 71.145 70.7532C70.7878 71.0077 70.5361 71.3469 70.392 71.775C70.2479 72.2031
                70.1748 72.6434 70.1748 73.0957C70.1748 73.6813 70.2601 74.1922 70.4326 74.6284C70.6051 75.0646
                70.871 75.3918 71.2323 75.6078C71.5936 75.8239 71.9853 75.9309 72.4074 75.9309C72.9209 75.9309
                73.3553 75.7835 73.7105 75.4887C74.0657 75.1939 74.3072 74.7577 74.431 74.1781L74.429 74.1801ZM79.7953
                74.9879L80.6742 75.0949C80.5361 75.6058 80.2784 76.0016 79.9049 76.2823C79.5294 76.563 79.0525 76.7044
                78.47 76.7044C77.7373 76.7044 77.1568 76.4802 76.7265 76.0299C76.2962 75.5816 76.0831 74.9515 76.0831
                74.1397C76.0831 73.3279 76.3003 72.6494 76.7346 72.187C77.169 71.7246 77.7312 71.4923 78.4233
                71.4923C79.1154 71.4923 79.6411 71.7185 80.0653 72.1729C80.4895 72.6272 80.7026 73.2653 80.7026
                74.0893C80.7026 74.1397 80.7026 74.2145 80.6985 74.3154H76.9599C76.9924 74.8627 77.1466 75.2827
                77.4267 75.5735C77.7068 75.8643 78.0559 76.0097 78.474 76.0097C78.7846 76.0097 79.0504 75.9289
                79.2717 75.7653C79.4909 75.6038 79.6654 75.3433 79.7953 74.9859V74.9879ZM77.0066
                73.6208H79.8055C79.7689 73.2007 79.6614 72.8877 79.4848 72.6777C79.2148 72.3526 78.8637 72.189
                78.4334 72.189C78.0437 72.189 77.7149 72.3183 77.4491 72.5788C77.1832 72.8393 77.035 73.1866 77.0086
                73.6208H77.0066ZM81.7296 76.5933V71.6074H82.4947V72.3627C82.6896 72.0093 82.8702 71.775 83.0346
                71.662C83.199 71.5489 83.3817 71.4923 83.5806 71.4923C83.8668 71.4923 84.157 71.5832 84.4533
                71.7649L84.1611 72.5485C83.954 72.4253 83.745 72.3647 83.538 72.3647C83.3533 72.3647 83.1848
                72.4192 83.0366 72.5323C82.8885 72.6434 82.7829 72.7968 82.72 72.9947C82.6267 73.2956 82.5779
                73.6228 82.5779 73.9802V76.5913H81.7296V76.5933ZM88.2285 75.9774C87.9139 76.2439 87.6115 76.4317
                87.3192 76.5408C87.0289 76.6498 86.7164 76.7044 86.3835 76.7044C85.8335 76.7044 85.4093 76.5711
                85.113 76.3025C84.8166 76.0339 84.6685 75.6926 84.6685 75.2767C84.6685 75.0323 84.7233 74.8102
                84.8369 74.6082C84.9486 74.4063 85.0947 74.2447 85.2753 74.1216C85.456 73.9984 85.6589 73.9075 85.8863
                73.8449C86.0527 73.8005 86.3044 73.7581 86.6413 73.7177C87.3273 73.6369 87.8327 73.54 88.1554
                73.4269C88.1594 73.3118 88.1594 73.2371 88.1594 73.2068C88.1594 72.8635 88.0783 72.6191 87.9179
                72.4798C87.7008 72.288 87.378 72.193 86.9498 72.193C86.5499 72.193 86.2557 72.2637 86.0649
                72.401C85.8741 72.5404 85.734 72.7867 85.6427 73.1401L84.8126 73.0271C84.8877 72.6737 85.0115 72.3889
                85.186 72.1708C85.3606 71.9527 85.6082 71.7851 85.937 71.668C86.2638 71.5509 86.6433 71.4923 87.0736
                71.4923C87.5039 71.4923 87.8489 71.5428 88.1168 71.6418C88.3847 71.7427 88.5816 71.8679 88.7075
                72.0194C88.8333 72.1708 88.9206 72.3627 88.9713 72.5949C88.9997 72.7383 89.0139 72.9988 89.0139
                73.3744V74.5012C89.0139 75.2867 89.0322 75.7835 89.0687 75.9915C89.1053 76.1995 89.1763 76.3994
                89.2839 76.5893H88.3969C88.3096 76.4136 88.2528 76.2096 88.2264 75.9733L88.2285 75.9774ZM86.9924
                70.1878L86.4586 70.9855H85.4864L86.5053 69.6708H87.4105L88.472 70.9855H87.51L86.9904
                70.1878H86.9924ZM88.1574 74.0893C87.8489 74.2145 87.3862 74.3215 86.7691 74.4083C86.4201 74.4588
                86.1724 74.5153 86.0283 74.5779C85.8842 74.6405 85.7726 74.7314 85.6934 74.8526C85.6143 74.9737
                85.5757 75.107 85.5757 75.2544C85.5757 75.4806 85.661 75.6684 85.8335 75.8179C86.006 75.9673 86.2556
                76.044 86.5865 76.044C86.9173 76.044 87.2055 75.9733 87.4592 75.83C87.7129 75.6866 87.9017 75.4927
                88.0214 75.2464C88.1128 75.0545 88.1574 74.7738 88.1574 74.4002V74.0913V74.0893ZM90.3332
                76.5933V71.6074H91.0923V72.3061C91.2506 72.0618 91.4576 71.8659 91.7194 71.7165C91.9813 71.567 92.2776
                71.4923 92.6104 71.4923C92.9819 71.4923 93.2863 71.5691 93.5238 71.7225C93.7612 71.876 93.9297 72.0901
                94.0271 72.3667C94.4229 71.7851 94.9405 71.4923 95.5757 71.4923C96.073 71.4923 96.4546 71.6296 96.7225
                71.9043C96.9904 72.1789 97.1244 72.5989 97.1244 73.1684V76.5913H96.28V73.4511C96.28 73.1139 96.2516
                72.8695 96.1968 72.7201C96.142 72.5707 96.0405 72.4515 95.8964 72.3607C95.7523 72.2698 95.5818 72.2254
                95.387 72.2254C95.0338 72.2254 94.7416 72.3425 94.5102 72.5747C94.2768 72.809 94.1611 73.1805 94.1611
                73.6955V76.5933H93.3107V73.3542C93.3107 72.9786 93.2417 72.6979 93.1036 72.5101C92.9656 72.3223 92.7383
                72.2294 92.4237 72.2294C92.1842 72.2294 91.963 72.292 91.76 72.4172C91.5571 72.5424 91.4089 72.7262
                91.3176 72.9665C91.2262 73.2068 91.1816 73.5541 91.1816 74.0085V76.5953H90.3332V76.5933ZM98.3888
                70.6805V69.7092H99.2372V70.6805H98.3888ZM98.3888 76.5933V71.6074H99.2372V76.5933H98.3888ZM103.802
                74.7657L104.638 74.8728C104.547 75.4463 104.313 75.8946 103.938 76.2177C103.562 76.5408 103.1 76.7044
                102.554 76.7044C101.868 76.7044 101.316 76.4822 100.899 76.0359C100.483 75.5897 100.274 74.9495
                100.274 74.1175C100.274 73.5783 100.364 73.1078 100.542 72.704C100.721 72.3001 100.995 71.9972
                101.36 71.7952C101.728 71.5933 102.125 71.4923 102.558 71.4923C103.102 71.4923 103.546 71.6296
                103.893 71.9043C104.24 72.1789 104.461 72.5666 104.559 73.0715L103.733 73.1987C103.654 72.8635
                103.516 72.6111 103.315 72.4435C103.116 72.2738 102.874 72.191 102.59 72.191C102.162 72.191 101.815
                72.3445 101.547 72.6494C101.279 72.9544 101.145 73.437 101.145 74.0973C101.145 74.7577 101.275 75.2544
                101.533 75.5573C101.79 75.8603 102.127 76.0137 102.543 76.0137C102.876 76.0137 103.154 75.9128
                103.378 75.7088C103.601 75.5048 103.743 75.1918 103.802 74.7698V74.7657ZM108.634 75.9774C108.32
                76.2439 108.017 76.4317 107.725 76.5408C107.435 76.6498 107.122 76.7044 106.789 76.7044C106.239
                76.7044 105.815 76.5711 105.519 76.3025C105.223 76.0339 105.074 75.6926 105.074 75.2767C105.074
                75.0323 105.129 74.8102 105.243 74.6082C105.355 74.4063 105.501 74.2447 105.681 74.1216C105.862
                73.9984 106.065 73.9075 106.292 73.8449C106.459 73.8005 106.71 73.7581 107.047 73.7177C107.733 73.6369
                108.239 73.54 108.561 73.4269C108.565 73.3118 108.565 73.2371 108.565 73.2068C108.565 72.8635 108.484
                72.6191 108.324 72.4798C108.107 72.288 107.784 72.193 107.356 72.193C106.956 72.193 106.662 72.2637
                106.471 72.401C106.28 72.5404 106.14 72.7867 106.049 73.1401L105.219 73.0271C105.294 72.6737 105.417
                72.3889 105.592 72.1708C105.767 71.9527 106.014 71.7851 106.343 71.668C106.67 71.5509 107.049 71.4923
                107.48 71.4923C107.91 71.4923 108.255 71.5428 108.523 71.6418C108.791 71.7427 108.988 71.8679 109.113
                72.0194C109.239 72.1708 109.327 72.3627 109.377 72.5949C109.406 72.7383 109.42 72.9988 109.42
                73.3744V74.5012C109.42 75.2867 109.438 75.7835 109.475 75.9915C109.511 76.1995 109.582 76.3994 109.69
                76.5893H108.803C108.716 76.4136 108.659 76.2096 108.632 75.9733L108.634 75.9774ZM108.563
                74.0893C108.255 74.2145 107.792 74.3215 107.175 74.4083C106.826 74.4588 106.578 74.5153 106.434
                74.5779C106.29 74.6405 106.179 74.7314 106.099 74.8526C106.02 74.9737 105.982 75.107 105.982
                75.2544C105.982 75.4806 106.067 75.6684 106.239 75.8179C106.412 75.9673 106.662 76.044 106.992
                76.044C107.323 76.044 107.611 75.9733 107.865 75.83C108.119 75.6866 108.308 75.4927 108.427
                75.2464C108.519 75.0545 108.563 74.7738 108.563 74.4002V74.0913V74.0893ZM110.96
                76.5933V75.63H111.929V76.5933C111.929 76.9467 111.866 77.2334 111.74 77.4495C111.614 77.6676 111.415
                77.8352 111.141 77.9544L110.906 77.5929C111.084 77.5141 111.216 77.399 111.301 77.2476C111.387 77.0961
                111.433 76.878 111.443 76.5933H110.962H110.96ZM115.907 74.3821L116.771 74.3073C116.812 74.6527
                116.907 74.9334 117.057 75.1555C117.208 75.3756 117.439 75.5553 117.754 75.6906C118.068 75.8259
                118.421 75.8946 118.815 75.8946C119.164 75.8946 119.473 75.8421 119.741 75.7391C120.009 75.6361
                120.207 75.4947 120.337 75.313C120.467 75.1333 120.534 74.9374 120.534 74.7233C120.534 74.5093
                120.471 74.3195 120.345 74.1579C120.22 73.9964 120.013 73.8611 119.722 73.752C119.538 73.6793 119.126
                73.5683 118.49 73.4168C117.855 73.2653 117.411 73.122 117.155 72.9867C116.824 72.815 116.578 72.601
                116.416 72.3465C116.254 72.0921 116.172 71.8053 116.172 71.4903C116.172 71.143 116.272 70.8179
                116.471 70.517C116.67 70.2141 116.958 69.9859 117.34 69.8284C117.719 69.6708 118.143 69.5941 118.61
                69.5941C119.124 69.5941 119.574 69.6769 119.966 69.8405C120.358 70.004 120.658 70.2464 120.869
                70.5654C121.08 70.8845 121.194 71.246 121.208 71.6498L120.329 71.7165C120.282 71.2823 120.122
                70.9531 119.85 70.731C119.578 70.5089 119.176 70.3978 118.645 70.3978C118.113 70.3978 117.687
                70.4988 117.435 70.7007C117.181 70.9027 117.055 71.145 117.055 71.4317C117.055 71.6781 117.145
                71.8821 117.323 72.0416C117.5 72.2011 117.961 72.3647 118.703 72.5323C119.446 72.6999 119.958
                72.8453 120.236 72.9705C120.638 73.1543 120.936 73.3885 121.127 73.6733C121.318 73.958 121.415
                74.2831 121.415 74.6527C121.415 75.0222 121.31 75.3635 121.098 75.6886C120.887 76.0117 120.585
                76.2641 120.189 76.4439C119.795 76.6236 119.351 76.7145 118.856 76.7145C118.231 76.7145 117.705
                76.6236 117.283 76.4418C116.861 76.2601 116.528 75.9875 116.286 75.622C116.045 75.2585 115.919
                74.8445 115.907 74.3841V74.3821ZM122.797 76.5933V75.63H123.765V76.5933H122.797ZM124.589
                76.5933L127.246 69.7092H128.233L131.064 76.5933H130.021L129.213 74.5093H126.321L125.562
                76.5933H124.589ZM126.587 73.7662H128.933L128.21 71.8599C127.991 71.2803 127.827 70.8057 127.719
                70.4321C127.63 70.8744 127.506 71.3126 127.346 71.7468L126.587 73.7662ZM131.929
                76.5933V75.63H132.897V76.5933H131.929Z"
                fill="white" />
            </g>
            <defs>
              <clipPath id="clip0_206_250">
                <rect width="144.198" height="80" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </a>
      </div>
      <div class="header-menu">
        <svg id="header-menu" onclick="" viewBox="0 0 100 100" width="80" class="">
          <path class="line top"
                d="m 70,33 h -40 c 0,0 -8.5,-0.149796 -8.5,8.5 0,8.649796 8.5,8.5 8.5,8.5 h 20 v -20">
          </path>
          <path class="line middle" d="m 70,50 h -40">
          </path>
          <path class="line bottom"
                d="m 30,67 h 40 c 0,0 8.5,0.149796 8.5,-8.5 0,-8.649796 -8.5,-8.5 -8.5,-8.5 h -20 v 20">
          </path>
        </svg>
      </div>
    </div>
    <div class="menu-search_i18n">
      <ul class="menu-navbar">
        <li v-for="menuitem in MenuData" :key="menuitem.id" class="nav-item"
            :class="{ dropdown: menuitem.childs?.length > 0 }">
          <a
            v-if="menuitem.childs?.length > 0"
            class="nav-link toggle-dropdown"
            href="#"
            id="navbarScrollingDropdown"
            role="button"
            data-toggle="dropdown"
            aria-expanded="false">
            {{ menuitem.label }}
          </a>
          <a
            v-else
            class="nav-link"
            :href="menuitem.link">
            {{ menuitem.label }}
          </a>
          <ul v-if="menuitem.childs?.length > 0"
              class="dropdown-menu"
              :class="{ 'scrollable-dropdown': menuitem.class === 'menu-produts' }">
            <li v-for="menuchild in menuitem.childs" :key="menuchild.id" class="dropdown-item-container">
              <div v-if="menuchild.childs?.length > 0" class="item-products-accordion static-menu">
                <div v-if="menuchild.class === 'item-products open-menu'">
                  <a class="item-link" :href="menuchild.link">{{menuchild.label}}</a>
                  <ul class="submenu-vertical">
                    <li v-for="child in menuchild.childs" :key="child.id" class="li-submenu">
                      <a :href="child.link">{{ child.label }}</a>
                    </li>
                  </ul>
                </div>
                <div v-else>
                  <p class="submenu-label-static">
                    {{ menuchild.alternative || menuchild.label }}
                  </p>
                  <ul class="submenu-vertical">
                    <li v-for="child in menuchild.childs" :key="child.id" class="li-submenu">
                      <a :href="child.link">{{ child.label }}</a>
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else
                   :class="{ 'item-products': menuchild.class === 'item-products',
                    'item-hr': menuchild.class === 'item-products item-hr' }">
                <a @click="navigateToCategoryWithSearch($event, menuchild.link)" :href="menuchild.link"
                   :style="!isEnabled(menuchild.label) ? 'opacity: 0.4; filter: grayscale(1);' : ''">
                  <p>{{ menuchild.alternative || menuchild.label }}</p>
                  <div v-if="menuchild.featureImage" class="content-image-nav">
                    <img :src="menuchild.featureImage.fullUrl" class="image" />
                  </div>
                </a>
              </div>
            </li>
          </ul>
        </li>
      </ul>
      <div class="content-user">
        <a :href="isAuthenticated ? '/logout' : '/login'">
          <svg v-if="!isAuthenticated" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-user">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="10" r="3"/>
            <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
          </svg>
          <svg v-if="isAuthenticated" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-log-out preview-icon"><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg>
        </a>
      </div>
    </div>
    <div class="search_i18n">
      <div id="search" class="search">
        <div class="content-search">
          <div class="search-wrapper" ref="searchContainer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="21"
              viewBox="0 0 21 21"
              fill="none"
              @click="toggleMenu"
              style="cursor: pointer;"
            >
              <path d="M8.70703 1C12.9631 1.0002 16.4131 4.45089 16.4131 8.70703C16.4129 12.963 12.963 16.4129 8.70703 16.4131C4.45089 16.4131 1.0002 12.9631 1 8.70703C1 4.45076 4.45076 1 8.70703 1Z" stroke="#333333" stroke-width="2" stroke-miterlimit="10" />
              <path d="M14.8667 14.8667L20 20" stroke="#333333" stroke-width="2" stroke-miterlimit="10" />
            </svg>

            <!-- Removido o "(searchHistory.length > 0 || query)" da condição principal para que a caixa apareça ao clicar no ícone -->
            <div class="search-box" :class="{ 'is-active': isMenuOpen }">
              <div class="input-field-group">
                <form :action="searchAction" method="GET" class="search-form" @submit.prevent="handleSearch">
                  <input
                    ref="searchInput"
                    v-model="query"
                    type="text"
                    name="q"
                    :placeholder="searchPlaceholder"
                    @focus="isMenuOpen = true"
                    @keyup.enter.prevent="handleSearch"
                    class="nav-search-input"
                    autocomplete="off"
                  />
                  <button
                    v-if="query"
                    type="button"
                    @click="clearSearch"
                    class="clear-input-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M15.3535 0.353516L0.353516 15.3535" stroke="#333333" stroke-miterlimit="10"/>
                      <path d="M0.353516 0.353516L15.3535 15.3535" stroke="#333333" stroke-miterlimit="10"/>
                    </svg>
                  </button>
                </form>
              </div>

              <!-- Transição suave para o dropdown de histórico -->
              <transition name="slide-down">
                <div v-if="isMenuOpen && searchHistory.length > 0" class="search-dropdown">
                  <div class="history-section">
                    <ul class="history-list">
                      <li
                        v-for="(item, index) in searchHistory"
                        :key="index"
                        @mousedown="selectHistory(item)"
                        class="history-item">
              <span class="clock-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <!-- (caminho do relógio mantido igual) -->
                </svg>
              </span>
                        <span class="item-text">{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mobile-menu shadow-sm">
      <div class="search_i18n-mobile">
        <div id="search" class="search">
          <div class="content-search">
            <div class="search-wrapper">
              <div class="search-box">
        <span class="search-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21"
               viewBox="0 0 21 21" fill="none">
           <path d="M8.70703 1C12.9631 1.0002 16.4131 4.45089 16.4131
                    8.70703C16.4129 12.963 12.963 16.4129 8.70703
                    16.4131C4.45089 16.4131 1.0002 12.9631 1 8.70703C1 4.45076
                    4.45076 1 8.70703 1Z"
                 stroke="#333333" stroke-width="2" stroke-miterlimit="10" />
           <path d="M14.8667 14.8667L20 20" stroke="#333333" stroke-width="2"
                 stroke-miterlimit="10" />
          </svg>
        </span>
                <form :action="searchAction" method="GET" class="search-input">
                  <input
                    ref="searchInput"
                    v-model="query"
                    type="text"
                    name="q"
                    id="search-mobile"
                    class="form-control search-input"
                    :placeholder="searchPlaceholder"
                    @keyup.enter.prevent="handleSearch"
                    autocomplete="off" />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ul class="navbar-nav">
        <li
          v-for="menuitem in MenuData"
          :key="menuitem.id"
          class="nav-item"
          :class="{ 'dropdown-open': menuitem.childs?.length > 0 && menuitem.isOpen }">
          <a
            class="nav-link"
            :href="menuitem.childs?.length > 0 ? '#' : menuitem.link"
            @click.prevent="toggleDropdown(menuitem)"
            v-if="menuitem.childs?.length > 0"
            href="#"
            role="button">
            {{ menuitem.label }}
            <span v-if="menuitem.childs?.length > 0" class="arrow-icon"
                  :class="{ 'rotate': menuitem.isOpen }">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7"
               viewBox="0 0 12 7" fill="none">
             <path d="M1 1L6 6L11 1" stroke="#333333" stroke-miterlimit="10" />
          </svg>
            </span>
          </a>
          <a
            v-else
            class="nav-link"
            :href="menuitem.link">
            {{ menuitem.label }}
          </a>
          <ul v-if="menuitem.childs?.length > 0" class="dropdown-menu">
            <li
              v-for="menuchild in menuitem.childs"
              :key="menuchild.id"
              class="dropdown-item"
              id="drop-item">
              <div v-if="menuchild.childs.length > 0" class="div-child">
                <div v-if="menuchild.class === 'item-products open-menu'">
                  <a :href="menuchild.link">{{menuchild.label}}</a>
                  <ul class="submenu-vertical">
                    <li v-for="child in menuchild.childs" :key="child.id"
                        class="li-submenu">
                      <a :href="child.link">{{ child.label }}</a>
                    </li>
                  </ul>
                </div>
                <div v-else>
                  <p class="submenu-label-static">
                    {{ menuchild.alternative || menuchild.label }}
                  </p>
                  <ul class="submenu-vertical">
                    <li v-for="child in menuchild.childs" :key="child.id"
                        class="li-submenu">
                      <a :href="child.link">{{ child.label }}</a>
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else>
                <a
                  :href="menuchild.link">
                  <p>{{ menuchild.label }}</p>
                </a>
              </div>
            </li>
          </ul>
        </li>
        <li class="nav-item">
          <a class="nav-link" :href="isAuthenticated ? '/logout' : '/login'">
            {{isAuthenticated ? 'logout' : 'login'}}
            <svg v-if="!isAuthenticated" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-user">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="10" r="3"/>
              <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
            </svg>
            <svg v-if="isAuthenticated" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-log-out preview-icon"><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg>
          </a>
        </li>
      </ul>
    </div>
  </nav>

</template>

<style scoped>

  .item-products-accordion {
    display: block;
    width: 100%;
    a{
      text-decoration: none;
      color: #000;
    }
  }
  .item-products{
    a{
      text-decoration: none;
      color: #000;
      p{
        margin:0 ;
      }
    }

  }
  .item-hr{
    border-bottom: 1px solid #DEDEDE;
    a{
      text-decoration: none;
      color: #000;
    }
  }

  .submenu-trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px 10px 0;
    cursor: pointer;
    margin: 0;
    width: 100%;
  }

  .submenu-vertical {
    list-style: none;
    padding: 0;
    margin: 0;
    display: block;
    position: static;
    border: none;
    box-shadow: none;

  }

  .submenu-vertical li a {
    display: block;
    padding: 0;
    color: #000;
    font-size: 14px;
    text-decoration: none;
  }

  .rotate {
    transform: rotate(90deg);
    transition: transform 0.2s ease;
  }

  /* Ajuste para garantir que o item pai não corte o conteúdo */
  .dropdown-menu {
    min-width: 300px;
    height: auto;
    max-height: 80vh; /* Se for muito longo, permite scroll no menu todo */
    overflow-y: auto;
  }



  /* Quando estiver ativa, revela a caixa suavemente */
  .search-box.is-active {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  /* Transição do Vue para elementos internos (opcional) */
  .slide-down-enter-active, .slide-down-leave-active {
    transition: max-height 0.3s ease, opacity 0.3s ease;
  }
  .slide-down-enter-from, .slide-down-leave-to {
    opacity: 0;
    max-height: 0;
  }
</style>
